import { z } from 'zod';

export const config = {
  IS_MODAL: 'IS_MODAL',
  FAILURE_URL: 'FAILURE_URL',
  SUCCESS_URL: 'SUCCESS_URL',
  SETTLEMENT_TYPE: 'SETTLEMENT_TYPE',
  SHIPPING_CALLBACK_URL: 'SHIPPING_CALLBACK_URL',
  BRANDING: 'BRANDING',
  FULFILLMENT_TYPE: 'FULFILLMENT_TYPE',
  TAX_TYPE: 'TAX_TYPE',
  EXPRESS_TERMINAL_ID: 'EXPRESS_TERMINAL_ID',
  TIPPING: 'TIPPING',
};

export const settlementTypes = {
  AUTH_CAPTURE: 'AuthCapture',
  SALE: 'Sale',
};

export const automationIds = {
  PAGE_HEADER: 'page_header',
  SIDEBAR: 'sidebar',
  PAYMENT_CREDENTIAL_SELECT: 'payment-cred-config-select',
  PROFILE_EXPANDER_DOWN: 'profileExpanderDown',
  PROFILE_EXPANDER_UP: 'profileExpanderUp',
  VIEW_PROFILE: 'viewProfile',
  LOG_OUT: 'logOut',
  CURRENT_PASS: 'currentPass',
  NEW_PASS: 'newPass',
  CONFIRM_PASS: 'confirmPass',
  CHANGE_PASS_BUTTON: 'changePasswordButton',
};

export const tabs = {
  CONFIGURATION: 'settings',
  INSIGHTS: 'insights',
  AGENT_HUB: 'agent-hub',
  PAYLINKS: 'paylinks',
  CARD_ON_FILE: 'card-on-file',
} as const;

export type MainTab = keyof typeof tabs;
export type IndexPath = `/${(typeof tabs)[MainTab]}`;

export const paths = {
  INDEX: '/',
  LOGIN: 'login',
  ONBOARDING: 'onboarding',
  PAYMENTS_AND_PROCESSING: 'payments-and-processing',
  BRANDING: 'branding',
  MERCHANT_DOMAINS: 'merchant-domains',
  FULFILLMENT: 'fulfillment',
  MERCHANT_WEBHOOKS: 'merchant-webhooks',
  PAYLINKS: 'paylinks',
  TIPPING: 'tipping',
  CART_EDITS: 'cart-edits',
  SHOPPER_CONSENT: 'shopper-consent',
  FEES: 'fees',
  API_KEYS: 'api-keys',
  CREATE_CHILD_MERCHANT: 'create-child-merchant',
  CLICK_TO_PAY: 'click-to-pay',
  FLEX_PAY: 'flex-pay',
  AGENTIC_CHECKOUT: 'agentic-checkout',
  AUDIT_LOGS: 'audit-logs',
  MERCHANT_USERS: 'merchant-users',
  PORTAL_USERS: 'portal-users',
  PORTAL_USERS_ROLES: 'roles',
  PORTAL_USERS_AUDIT_LOGS: 'audit-logs',
  ADMIN: 'admin',
  CREATE_MERCHANT: 'create-merchant',
  SHOPIFY_PROVISIONING: 'shopify-provisioning',
  INSIGHTS: 'insights',
} as const;

export const publicPaths = [`/${paths.ONBOARDING}`];

export const configurationPaths = {
  SETTINGS: `/${tabs.CONFIGURATION}`,
  PAYMENTS_AND_PROCESSING: `/${tabs.CONFIGURATION}/${paths.PAYMENTS_AND_PROCESSING}`,
  MERCHANT_DOMAINS: `/${tabs.CONFIGURATION}/${paths.MERCHANT_DOMAINS}`,
  BRANDING: `/${tabs.CONFIGURATION}/${paths.BRANDING}`,
  FULFILLMENT: `/${tabs.CONFIGURATION}/${paths.FULFILLMENT}`,
  MERCHANT_WEBHOOKS: `/${tabs.CONFIGURATION}/${paths.MERCHANT_WEBHOOKS}`,
  PAYLINKS: `/${tabs.CONFIGURATION}/${paths.PAYLINKS}`,
  TIPPING: `/${tabs.CONFIGURATION}/${paths.TIPPING}`,
  CART_EDITS: `/${tabs.CONFIGURATION}/${paths.CART_EDITS}`,
  SHOPPER_CONSENT: `/${tabs.CONFIGURATION}/${paths.SHOPPER_CONSENT}`,
  FEES: `/${tabs.CONFIGURATION}/${paths.FEES}`,
  API_KEYS: `/${tabs.CONFIGURATION}/${paths.API_KEYS}`,
  CREATE_CHILD_MERCHANT: `/${tabs.CONFIGURATION}/${paths.CREATE_CHILD_MERCHANT}`,
  CLICK_TO_PAY: `/${tabs.CONFIGURATION}/${paths.CLICK_TO_PAY}`,
  FLEX_PAY: `/${tabs.CONFIGURATION}/${paths.FLEX_PAY}`,
  AGENTIC_CHECKOUT: `/${tabs.CONFIGURATION}/${paths.AGENTIC_CHECKOUT}`,
  AUDIT_LOGS: `/${tabs.CONFIGURATION}/${paths.AUDIT_LOGS}`,
  MERCHANT_USERS: `/${tabs.CONFIGURATION}/${paths.MERCHANT_USERS}`,
  PORTAL_USERS: `/${paths.PORTAL_USERS}`,
  PORTAL_USERS_ROLES: `/${paths.PORTAL_USERS}/${paths.PORTAL_USERS_ROLES}`,
  PORTAL_USERS_AUDIT_LOGS: `/${paths.PORTAL_USERS}/${paths.PORTAL_USERS_AUDIT_LOGS}`,
  ADMIN: `/${paths.ADMIN}`,
  ADMIN_CREATE_MERCHANT: `/${paths.ADMIN}/${paths.CREATE_MERCHANT}`,
  ADMIN_SHOPIFY_PROVISIONING: `/${paths.ADMIN}/${paths.SHOPIFY_PROVISIONING}`,
  ADMIN_SHOPIFY_PROVISIONING_DETAIL: `/${paths.ADMIN}/${paths.SHOPIFY_PROVISIONING}/:merchantId`,
} as const;
export type ConfigurationPaths = typeof configurationPaths;
export type ConfigurationPath = ConfigurationPaths[keyof ConfigurationPaths];

/** Parent/child configuration tables and child-only bootstrap first-page fetch — keep identical so RTK can cache one request. */
export const CONFIG_MERCHANT_LIST_PAGE_SIZE = 20;

export const insightsPaths = {
  INSIGHTS: `/${tabs.INSIGHTS}`,
} as const;

export type InsightsPaths = typeof insightsPaths;
export type InsightsPath = InsightsPaths[keyof InsightsPaths];

export const paylinkPaths = {
  SEND_PAYLINKS: `/${tabs.PAYLINKS}`,
  VIEW_ALL: `/${tabs.PAYLINKS}/view-all`,
  CUSTOMERS: `/${tabs.PAYLINKS}/customers`,
} as const;

export type PaylinkPaths = typeof paylinkPaths;
export type PaylinkPath = PaylinkPaths[keyof PaylinkPaths];

export const cardOnFilePaths = {
  CREATE_TRANSACTION: `/${tabs.CARD_ON_FILE}`,
  VIEW_ALL: `/${tabs.CARD_ON_FILE}/view-all`,
  CUSTOMERS: `/${tabs.CARD_ON_FILE}/customers`,
} as const;

export type CardOnFilePaths = typeof cardOnFilePaths;
export type CardOnFilePath = CardOnFilePaths[keyof CardOnFilePaths];

export const agentHubPaths = {
  AGENT_HUB: `/${tabs.AGENT_HUB}`,
  ONBOARDING: `/${tabs.AGENT_HUB}/onboarding`,
  DASHBOARD: `/${tabs.AGENT_HUB}/dashboard`,
  AGENTS: `/${tabs.AGENT_HUB}/agents`,
  PROMPT_MANAGEMENT: `/${tabs.AGENT_HUB}/prompt-management`,
  ENGAGEMENT: `/${tabs.AGENT_HUB}/engagement`,
  ESCALATION: `/${tabs.AGENT_HUB}/escalation`,
  SUPPORT: `/${tabs.AGENT_HUB}/support`,
  CHECKOUT_MODE: `/${tabs.AGENT_HUB}/checkout-mode`,
  WORKFLOWS: `/${tabs.AGENT_HUB}/workflows`,
  CATALOG: `/${tabs.AGENT_HUB}/catalog`,
  PROVISIONING: `/${tabs.AGENT_HUB}/provisioning`,
  PROVISIONING_SHOPIFY: `/${tabs.AGENT_HUB}/provisioning/shopify`,
  PROVISIONING_BIGCOMMERCE: `/${tabs.AGENT_HUB}/provisioning/bigcommerce`,
  PROVISIONING_SQUARESPACE: `/${tabs.AGENT_HUB}/provisioning/squarespace`,
} as const;

export type AgentHubPaths = typeof agentHubPaths;
export type AgentHubPath = AgentHubPaths[keyof AgentHubPaths];

export type PortalPath =
  IndexPath | ConfigurationPath | InsightsPath | PaylinkPath | CardOnFilePath | AgentHubPath;

export const documentationUrl = 'https://docs.skipify.com/docs';
export const webhooksDocumentationUrl = 'https://docs.skipify.com/docs/webhooks';
export const purchaseDetailsUrl =
  'https://docs.skipify.com/docs/creating-an-order-egc#create-order---purchase-details';

export const MERCHANT_PARTNER_ROLES = ['SUPERADMIN', 'MERCHANTPARTNER'];

export const CardBrandSchema = z.enum(['amex', 'mastercard', 'visa', 'discover']);
export type CardBrand = z.infer<typeof CardBrandSchema>;

export type StatusColor = { bg: string; text: string };

export const colorDisabled: StatusColor = { bg: '#E0E0E0', text: '#474747' };
export const colorWarning: StatusColor = { bg: '#FFFAEB', text: '#AC5F04' };
export const colorSuccess: StatusColor = { bg: '#ECFDF3', text: '#207A00' };
export const colorError: StatusColor = { bg: '#FEF3F2', text: '#BC3003' };
